import { type GridSortModel } from "@mui/x-data-grid-pro";
import type {
  GetInheritanceReuseCandidatesAPIRequest,
  IInheritanceReuseFilterCondition,
} from "@/features/inheritanceReuse/types";
import {
  SALE_RANK_TO_MIN_SCORE,
  SORTABLE_FIELD_TO_ORDERING,
} from "@/features/inheritanceReuse/utils/constants";

/** 数値文字列を number へ。空文字・非数値は undefined。 */
const toNumberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** 文字列フィルタを送信値へ。空文字は undefined（クエリに含めない）。 */
const toStringOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
};

/**
 * MUI の GridSortModel を DRF の ordering 値（snake_case）へ変換する。
 * 無料版 DataGrid は単一列ソートのため先頭要素のみ扱う。
 * ソート対象外の列・未ソート時は undefined。
 */
export const toOrdering = (
  sortModel: GridSortModel | undefined
): string | undefined => {
  if (sortModel === undefined || sortModel.length === 0) return undefined;
  const { field, sort } = sortModel[0];
  const orderingField = SORTABLE_FIELD_TO_ORDERING[field];
  if (orderingField === undefined || (sort !== "asc" && sort !== "desc")) {
    return undefined;
  }
  return sort === "desc" ? `-${orderingField}` : orderingField;
};

/**
 * 画面のフィルタ・ページ・ソートから API リクエストを組み立てる。
 * 未指定（空）の項目はキーごと省く（apiClient が undefined を送らないようにする）。
 * @param filter フィルタ条件
 * @param page 0 始まりのページ番号（API へは +1 して 1 始まりで送る）
 * @param pageSize ページサイズ
 * @param sortModel MUI ソートモデル
 */
export const buildCandidatesRequest = (
  filter: IInheritanceReuseFilterCondition,
  page: number,
  pageSize: number,
  sortModel: GridSortModel | undefined
): GetInheritanceReuseCandidatesAPIRequest => {
  const request: GetInheritanceReuseCandidatesAPIRequest = {
    prefectureName: filter.prefectureName,
    page: page + 1,
    pageSize,
  };

  const cityName = toStringOrUndefined(filter.cityName);
  if (cityName !== undefined) request.cityName = cityName;

  const realEstateType = toStringOrUndefined(filter.realEstateType);
  if (realEstateType !== undefined) request.realEstateType = realEstateType;

  const distanceCategory = toStringOrUndefined(filter.distanceCategory);
  if (distanceCategory !== undefined)
    request.distanceCategory = distanceCategory;

  const acquiredFrom = toStringOrUndefined(filter.acquiredFrom);
  if (acquiredFrom !== undefined) request.acquiredFrom = acquiredFrom;

  const acquiredTo = toStringOrUndefined(filter.acquiredTo);
  if (acquiredTo !== undefined) request.acquiredTo = acquiredTo;

  const inheritanceFrom = toStringOrUndefined(filter.inheritanceFrom);
  if (inheritanceFrom !== undefined) request.inheritanceFrom = inheritanceFrom;

  const inheritanceTo = toStringOrUndefined(filter.inheritanceTo);
  if (inheritanceTo !== undefined) request.inheritanceTo = inheritanceTo;

  const buildYearFrom = toNumberOrUndefined(filter.buildYearFrom);
  if (buildYearFrom !== undefined) request.buildYearFrom = buildYearFrom;

  const buildYearTo = toNumberOrUndefined(filter.buildYearTo);
  if (buildYearTo !== undefined) request.buildYearTo = buildYearTo;

  const minEstimatedLandValue = toNumberOrUndefined(
    filter.minEstimatedLandValue
  );
  if (minEstimatedLandValue !== undefined)
    request.minEstimatedLandValue = minEstimatedLandValue;

  const maxEstimatedLandValue = toNumberOrUndefined(
    filter.maxEstimatedLandValue
  );
  if (maxEstimatedLandValue !== undefined)
    request.maxEstimatedLandValue = maxEstimatedLandValue;

  // 「売却期待度★N以上」は表示用ランク。BE は min_sale_score（スコア下限）で受けるため、
  // ランク→対応する最小スコアへ変換して送る（SALE_RANK_TO_MIN_SCORE）。
  const saleRank = toNumberOrUndefined(filter.minSaleRank);
  if (saleRank !== undefined) {
    const minSaleScore = SALE_RANK_TO_MIN_SCORE[saleRank];
    // ★1以上（スコア0以上）は全件のためクエリを付けない（BE 既定 min_sale_score=0 と同義）。
    if (minSaleScore !== undefined && minSaleScore > 0) {
      request.minSaleScore = minSaleScore;
    }
  }

  const minInheritanceScore = toNumberOrUndefined(filter.minInheritanceScore);
  if (minInheritanceScore !== undefined)
    request.minInheritanceScore = minInheritanceScore;

  const maxOwnerCount = toNumberOrUndefined(filter.maxOwnerCount);
  if (maxOwnerCount !== undefined) request.maxOwnerCount = maxOwnerCount;

  // 真偽フィルタは ON のときだけ送る（OFF は絞り込み無し）。
  if (filter.isOccupant) request.isOccupant = true;
  if (filter.emptyHouse) request.emptyHouse = true;

  const ordering = toOrdering(sortModel);
  if (ordering !== undefined) request.ordering = ordering;

  return request;
};

/** 範囲・数値フィルタの検証結果（フィールド別のエラーメッセージ）。 */
export interface FilterRangeErrors {
  acquiredTo?: string;
  buildYearFrom?: string;
  buildYearTo?: string;
  inheritanceTo?: string;
  minInheritanceScore?: string;
  maxOwnerCount?: string;
  minEstimatedLandValue?: string;
  maxEstimatedLandValue?: string;
}

const NEGATIVE_MESSAGE = "0 以上の値を指定してください。";

/** 負数の数値文字列か（空・非数値は false）。 */
const isNegativeNumber = (value: string): boolean => {
  const n = toNumberOrUndefined(value);
  return n !== undefined && n < 0;
};

/**
 * 範囲（from <= to）と数値の非負をクライアント側で検証する（BE の validate と同義）。
 * 問題があれば該当フィールドにメッセージを返す。空（未指定）は検証しない。
 */
export const validateFilterRanges = (
  filter: IInheritanceReuseFilterCondition
): FilterRangeErrors => {
  const errors: FilterRangeErrors = {};

  if (
    filter.acquiredFrom !== "" &&
    filter.acquiredTo !== "" &&
    filter.acquiredFrom > filter.acquiredTo
  ) {
    errors.acquiredTo = "取得日（以降）以降の日付を指定してください。";
  }
  if (
    filter.inheritanceFrom !== "" &&
    filter.inheritanceTo !== "" &&
    filter.inheritanceFrom > filter.inheritanceTo
  ) {
    errors.inheritanceTo = "相続登記日（以降）以降の日付を指定してください。";
  }

  // 非負チェック（負数入力を弾く）。
  if (isNegativeNumber(filter.minInheritanceScore))
    errors.minInheritanceScore = NEGATIVE_MESSAGE;
  if (isNegativeNumber(filter.maxOwnerCount))
    errors.maxOwnerCount = NEGATIVE_MESSAGE;
  if (isNegativeNumber(filter.buildYearFrom))
    errors.buildYearFrom = NEGATIVE_MESSAGE;
  if (isNegativeNumber(filter.buildYearTo))
    errors.buildYearTo = NEGATIVE_MESSAGE;
  if (isNegativeNumber(filter.minEstimatedLandValue))
    errors.minEstimatedLandValue = NEGATIVE_MESSAGE;
  if (isNegativeNumber(filter.maxEstimatedLandValue))
    errors.maxEstimatedLandValue = NEGATIVE_MESSAGE;

  // 範囲（from <= to）。非負エラーが無い方を優先し、既存エラーは上書きしない。
  const buildFrom = toNumberOrUndefined(filter.buildYearFrom);
  const buildTo = toNumberOrUndefined(filter.buildYearTo);
  if (
    errors.buildYearTo === undefined &&
    buildFrom !== undefined &&
    buildTo !== undefined &&
    buildFrom > buildTo
  ) {
    errors.buildYearTo = "築年（下限）以上の値を指定してください。";
  }
  const minValue = toNumberOrUndefined(filter.minEstimatedLandValue);
  const maxValue = toNumberOrUndefined(filter.maxEstimatedLandValue);
  if (
    errors.maxEstimatedLandValue === undefined &&
    minValue !== undefined &&
    maxValue !== undefined &&
    minValue > maxValue
  ) {
    errors.maxEstimatedLandValue = "下限以上の値を指定してください。";
  }

  return errors;
};

/** 範囲エラーが1つでもあるか。 */
export const hasFilterRangeErrors = (errors: FilterRangeErrors): boolean =>
  Object.keys(errors).length > 0;
